<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ $i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ $i18n.t('pages.url.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </div>
        <h1 class="page-title">{{ $i18n.t('pages.url.title') }}</h1>
        <p class="page-subtitle">{{ $i18n.t('pages.url.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="converter-card">
        <div class="conversion-section">
          <div class="section-header">
            <span class="section-tag">TEXT → URL</span>
            <h3>{{ $i18n.t('pages.url.textToUrl') }}</h3>
          </div>
          <textarea
            v-model="textInput"
            :placeholder="$i18n.t('pages.url.inputPlaceholder.text')"
            class="input-area"
            rows="4"
          ></textarea>
          <button @click="encodeURL" class="action-btn">{{ $i18n.t('pages.url.encode') }}</button>
          <div v-if="encodedOutput" class="result-box">
            <div class="result-header">
              <span class="result-label">{{ $i18n.t('pages.url.outputPlaceholder.url') }}</span>
              <button @click="copyEncoded" class="copy-btn">{{ $i18n.locale === 'zh' ? '复制' : 'Copy' }}</button>
            </div>
            <pre class="result-value">{{ encodedOutput }}</pre>
          </div>
        </div>

        <div class="conversion-section">
          <div class="section-header">
            <span class="section-tag">URL → TEXT</span>
            <h3>{{ $i18n.t('pages.url.urlToText') }}</h3>
          </div>
          <textarea
            v-model="encodedInput"
            :placeholder="$i18n.t('pages.url.inputPlaceholder.url')"
            class="input-area"
            rows="4"
          ></textarea>
          <button @click="decodeURL" class="action-btn">{{ $i18n.t('pages.url.decode') }}</button>
          <div v-if="decodedOutput" class="result-box">
            <div class="result-header">
              <span class="result-label">{{ $i18n.t('pages.url.outputPlaceholder.text') }}</span>
              <button @click="copyDecoded" class="copy-btn">{{ $i18n.locale === 'zh' ? '复制' : 'Copy' }}</button>
            </div>
            <pre class="result-value text-result">{{ decodedOutput }}</pre>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'URLEncoderDecoderPage',
  inject: ['i18n'],
  data() {
    return {
      textInput: '',
      encodedInput: '',
      encodedOutput: '',
      decodedOutput: ''
    }
  },
  methods: {
    encodeURL() {
      if (!this.textInput.trim()) {
        return
      }
      try {
        this.encodedOutput = encodeURIComponent(this.textInput)
      } catch (e) {
        console.error(e)
      }
    },
    decodeURL() {
      if (!this.encodedInput.trim()) {
        return
      }
      try {
        this.decodedOutput = decodeURIComponent(this.encodedInput)
      } catch (e) {
        console.error(e)
      }
    },
    copyEncoded() {
      if (!this.encodedOutput) {
        return
      }
      navigator.clipboard.writeText(this.encodedOutput)
    },
    copyDecoded() {
      if (!this.decodedOutput) {
        return
      }
      navigator.clipboard.writeText(this.decodedOutput)
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.08) 0%, transparent 70%);
  top: 30%;
  right: -100px;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 700px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: var(--accent-primary);
}

.breadcrumb-sep {
  color: var(--border-color);
}

.breadcrumb-current {
  color: var(--accent-success);
}

.header-content {
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-success);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.converter-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.conversion-section {
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid var(--border-color);
}

.conversion-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.section-tag {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1px;
  padding: 4px 8px;
  background: var(--bg-secondary);
  border-radius: 4px;
  color: var(--accent-success);
}

.section-header h3 {
  font-family: var(--font-body);
  font-size: 18px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
}

.input-area {
  width: 100%;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  margin-bottom: 16px;
  transition: border-color 0.3s;
}

.input-area::placeholder {
  color: var(--text-secondary);
}

.input-area:focus {
  outline: none;
  border-color: var(--accent-success);
}

.action-btn {
  padding: 12px 28px;
  background: var(--accent-success);
  color: var(--bg-primary);
  border: none;
  border-radius: 10px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 240, 255, 0.3);
}

.result-box {
  margin-top: 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  overflow: hidden;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.result-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.copy-btn {
  padding: 6px 14px;
  background: var(--accent-success);
  color: var(--bg-primary);
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  opacity: 0.9;
}

.result-value {
  padding: 16px;
  font-family: var(--font-mono);
  font-size: 14px;
  line-height: 1.6;
  color: var(--accent-success);
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
}

.text-result {
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .page-container {
    padding: 24px 16px 40px;
  }

  .page-title {
    font-size: 36px;
  }

  .converter-card {
    padding: 24px;
  }
}
</style>
